import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CommunityMessage,
  CommunityMessagesState,
  SendMessageRequest,
  SendAdRequest,
  AdminMessageRequest,
  AdminAdRequest,
  UpdateMessageRequest,
  MessageQueryParams,
  MessagesListResponse,
  MessageResponse,
  UnreadCountResponse,
  CommunityMessagesApiError,
} from './types';

const initialState: CommunityMessagesState = {
  messages: [],
  currentMessage: null,
  advertisements: [],
  communityGroupMessages: [],
  communityGroupAdvertisements: [],
  unreadCount: 0,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },
};

// API base URL - adjust according to your backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Send message to community
export const sendMessage = createAsyncThunk<
  CommunityMessage,
  { messageData: SendMessageRequest; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/sendMessage', async ({ messageData, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/messages?userId=${userId}`, messageData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to send message',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get messages from community
export const getMessages = createAsyncThunk<
  MessagesListResponse,
  { communityId: string; params: MessageQueryParams; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getMessages', async ({ communityId, params, userId }, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    queryParams.append('userId', userId);

    const response = await axios.get(`${API_BASE_URL}/messages/community/${communityId}?${queryParams}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch messages',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get messages by community ID
export const getMessagesByCommunityId = createAsyncThunk<
  MessagesListResponse,
  { communityId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getMessagesByCommunityId', async ({ communityId }, { rejectWithValue }) => {
  try {
   
    
    const response = await axios.get(`${API_BASE_URL}/messages/community/${communityId}/messages`);
    
    
    if (response.data.success) {
      return response.data.data;
    } else {
      console.log('API returned success: false');
      return rejectWithValue({
        message: response.data.message || 'Failed to fetch messages by community ID',
        statusCode: response.data.statusCode || 500,
        error: response.data.error,
      });
    }
  } catch (error: any) {
   
    
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch messages by community ID',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get message by ID
export const getMessageById = createAsyncThunk<
  CommunityMessage,
  { messageId: string; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getMessageById', async ({ messageId, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/messages/${messageId}?userId=${userId}`);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch message',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Update message
export const updateMessage = createAsyncThunk<
  CommunityMessage,
  { messageId: string; updateData: UpdateMessageRequest; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/updateMessage', async ({ messageId, updateData, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/messages/${messageId}?userId=${userId}`, updateData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to update message',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Delete message
export const deleteMessage = createAsyncThunk<
  void,
  { messageId: string; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/deleteMessage', async ({ messageId, userId }, { rejectWithValue }) => {
  try {
    await axios.delete(`${API_BASE_URL}/messages/${messageId}?userId=${userId}`);
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to delete message',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Mark message as read
export const markMessageAsRead = createAsyncThunk<
  void,
  { messageId: string; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/markMessageAsRead', async ({ messageId, userId }, { rejectWithValue }) => {
  try {
    await axios.post(`${API_BASE_URL}/messages/${messageId}/read?userId=${userId}`);
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to mark message as read',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get unread message count
export const getUnreadMessageCount = createAsyncThunk<
  number,
  { communityId: string; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getUnreadMessageCount', async ({ communityId, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/messages/community/${communityId}/unread-count?userId=${userId}`);
    return response.data.data.unreadCount;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch unread count',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Send advertisement
export const sendAdvertisement = createAsyncThunk<
  CommunityMessage,
  { adData: SendAdRequest; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/sendAdvertisement', async ({ adData, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/messages/ads?userId=${userId}`, adData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to send advertisement',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get advertisements
export const getAdvertisements = createAsyncThunk<
  MessagesListResponse,
  { communityId: string; params: MessageQueryParams; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getAdvertisements', async ({ communityId, params, userId }, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    queryParams.append('userId', userId);

    const response = await axios.get(`${API_BASE_URL}/messages/community/${communityId}/ads?${queryParams}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch advertisements',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Send community group message (Admin only)
export const sendCommunityGroupMessage = createAsyncThunk<
  CommunityMessage,
  { messageData: AdminMessageRequest; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/sendCommunityGroupMessage', async ({ messageData, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/messages/community-groups?userId=${userId}`, messageData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to send community group message',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get community group messages
export const getCommunityGroupMessages = createAsyncThunk<
  MessagesListResponse,
  { communityGroupId: string; params: MessageQueryParams; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getCommunityGroupMessages', async ({ communityGroupId, params, userId }, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    queryParams.append('userId', userId);

    const response = await axios.get(`${API_BASE_URL}/messages/community-groups/${communityGroupId}?${queryParams}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch community group messages',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Send community group advertisement (Admin only)
export const sendCommunityGroupAdvertisement = createAsyncThunk<
  CommunityMessage,
  { adData: AdminAdRequest; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/sendCommunityGroupAdvertisement', async ({ adData, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/messages/community-groups/ads?userId=${userId}`, adData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to send community group advertisement',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get community group advertisements
export const getCommunityGroupAdvertisements = createAsyncThunk<
  MessagesListResponse,
  { communityGroupId: string; params: MessageQueryParams; userId: string },
  { rejectValue: CommunityMessagesApiError }
>('communityMessages/getCommunityGroupAdvertisements', async ({ communityGroupId, params, userId }, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    queryParams.append('userId', userId);

    const response = await axios.get(`${API_BASE_URL}/messages/community-groups/${communityGroupId}/ads?${queryParams}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch community group advertisements',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

const communityMessagesSlice = createSlice({
  name: 'communityMessages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentMessage: (state) => {
      state.currentMessage = null;
    },
    clearMessages: (state) => {
      state.messages = [];
      state.pagination = {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
      };
    },
    clearAdvertisements: (state) => {
      state.advertisements = [];
    },
    clearCommunityGroupMessages: (state) => {
      state.communityGroupMessages = [];
    },
    clearCommunityGroupAdvertisements: (state) => {
      state.communityGroupAdvertisements = [];
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number; total: number; totalPages: number }>) => {
      state.pagination = action.payload;
    },
    addMessage: (state, action: PayloadAction<CommunityMessage>) => {
      state.messages.unshift(action.payload);
      state.pagination.total += 1;
    },
    updateMessageInList: (state, action: PayloadAction<CommunityMessage>) => {
      const index = state.messages.findIndex(msg => msg._id === action.payload._id);
      if (index !== -1) {
        state.messages[index] = action.payload;
      }
      if (state.currentMessage?._id === action.payload._id) {
        state.currentMessage = action.payload;
      }
    },
    removeMessageFromList: (state, action: PayloadAction<string>) => {
      state.messages = state.messages.filter(msg => msg._id !== action.payload);
      if (state.currentMessage?._id === action.payload) {
        state.currentMessage = null;
      }
      state.pagination.total -= 1;
    },
  },
  extraReducers: (builder) => {
    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messages.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to send message';
      });

    // Get messages
    builder
      .addCase(getMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.data.messages;
        state.pagination = {
          page: action.payload.data.page,
          limit: action.payload.data.limit,
          total: action.payload.data.total,
          totalPages: Math.ceil(action.payload.data.total / action.payload.data.limit),
        };
      })
      .addCase(getMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch messages';
      });

    // Get messages by community ID
    builder
      .addCase(getMessagesByCommunityId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMessagesByCommunityId.fulfilled, (state, action) => {
        console.log('=== getMessagesByCommunityId.fulfilled ===');
        console.log('Full action payload:', action.payload);
        console.log('Action payload data:', action.payload.data);
        console.log('Messages:', action.payload.data?.messages);
        console.log('==========================================');
        
        state.loading = false;
        state.messages = action.payload.data.messages;
        state.pagination = {
          page: 1, // Default page since API doesn't return pagination info
          limit: action.payload.data.messages?.length || 10, // Use actual message count
          total: action.payload.data.total,
          totalPages: 1, // Since API returns all messages at once
        };
      })
      .addCase(getMessagesByCommunityId.rejected, (state, action) => {
        console.log('=== getMessagesByCommunityId.rejected ===');
        console.log('Error payload:', action.payload);
        console.log('Error message:', action.payload?.message);
        console.log('==========================================');
        
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch messages by community ID';
      });

    // Get message by ID
    builder
      .addCase(getMessageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMessageById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMessage = action.payload;
      })
      .addCase(getMessageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch message';
      });

    // Update message
    builder
      .addCase(updateMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.messages.findIndex(msg => msg._id === action.payload._id);
        if (index !== -1) {
          state.messages[index] = action.payload;
        }
        if (state.currentMessage?._id === action.payload._id) {
          state.currentMessage = action.payload;
        }
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update message';
      });

    // Delete message
    builder
      .addCase(deleteMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading = false;
        const messageId = action.meta.arg.messageId;
        state.messages = state.messages.filter(msg => msg._id !== messageId);
        if (state.currentMessage?._id === messageId) {
          state.currentMessage = null;
        }
        state.pagination.total -= 1;
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete message';
      });

    // Mark message as read
    builder
      .addCase(markMessageAsRead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markMessageAsRead.fulfilled, (state) => {
        state.loading = false;
        // Message marked as read successfully
      })
      .addCase(markMessageAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to mark message as read';
      });

    // Get unread message count
    builder
      .addCase(getUnreadMessageCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUnreadMessageCount.fulfilled, (state, action) => {
        state.loading = false;
        state.unreadCount = action.payload;
      })
      .addCase(getUnreadMessageCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch unread count';
      });

    // Send advertisement
    builder
      .addCase(sendAdvertisement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendAdvertisement.fulfilled, (state, action) => {
        state.loading = false;
        state.advertisements.unshift(action.payload);
      })
      .addCase(sendAdvertisement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to send advertisement';
      });

    // Get advertisements
    builder
      .addCase(getAdvertisements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdvertisements.fulfilled, (state, action) => {
        state.loading = false;
        state.advertisements = action.payload.data.messages;
        state.pagination = {
          page: action.payload.data.page,
          limit: action.payload.data.limit,
          total: action.payload.data.total,
          totalPages: Math.ceil(action.payload.data.total / action.payload.data.limit),
        };
      })
      .addCase(getAdvertisements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch advertisements';
      });

    // Send community group message
    builder
      .addCase(sendCommunityGroupMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendCommunityGroupMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.communityGroupMessages.unshift(action.payload);
      })
      .addCase(sendCommunityGroupMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to send community group message';
      });

    // Get community group messages
    builder
      .addCase(getCommunityGroupMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCommunityGroupMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.communityGroupMessages = action.payload.data.messages;
        state.pagination = {
          page: action.payload.data.page,
          limit: action.payload.data.limit,
          total: action.payload.data.total,
          totalPages: Math.ceil(action.payload.data.total / action.payload.data.limit),
        };
      })
      .addCase(getCommunityGroupMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch community group messages';
      });

    // Send community group advertisement
    builder
      .addCase(sendCommunityGroupAdvertisement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendCommunityGroupAdvertisement.fulfilled, (state, action) => {
        state.loading = false;
        state.communityGroupAdvertisements.unshift(action.payload);
      })
      .addCase(sendCommunityGroupAdvertisement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to send community group advertisement';
      });

    // Get community group advertisements
    builder
      .addCase(getCommunityGroupAdvertisements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCommunityGroupAdvertisements.fulfilled, (state, action) => {
        state.loading = false;
        state.communityGroupAdvertisements = action.payload.data.messages;
        state.pagination = {
          page: action.payload.data.page,
          limit: action.payload.data.limit,
          total: action.payload.data.total,
          totalPages: Math.ceil(action.payload.data.total / action.payload.data.limit),
        };
      })
      .addCase(getCommunityGroupAdvertisements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch community group advertisements';
      });
  },
});

export const {
  clearError,
  clearCurrentMessage,
  clearMessages,
  clearAdvertisements,
  clearCommunityGroupMessages,
  clearCommunityGroupAdvertisements,
  setPagination,
  addMessage,
  updateMessageInList,
  removeMessageFromList,
} = communityMessagesSlice.actions;

export default communityMessagesSlice.reducer;
